import { rankProductMatches } from '../../utils/rankProductMatches';
import MultiProductPicker from '../../components/UI/MultiProductPicker';
import { businessToday } from '../../utils/businessTime';
import React, { useState, useEffect, useRef } from 'react';
import ProductSourceCheckboxes, { DEFAULT_PRODUCT_TYPES, matchesProductTypes } from '../../components/UI/ProductSourceCheckboxes';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiPlus, FiTrash2, FiUser, FiPackage, FiDollarSign, FiSearch, FiCalendar, FiFileText, FiCreditCard, FiShoppingCart, FiMapPin } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useFacturas } from '../../hooks/useFacturas';
import { useClientes } from '../../hooks/useClientes';
import { useProductos } from '../../hooks/useProductos';
import { clientesService } from '../../services';
import { precioParaCategoria } from '../../utils';

const todayLocal = businessToday;

const FacturaForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const dropdownRef = useRef(null);

  const {
    createFactura,
    updateFactura,
    getFacturaById,
    isLoading: facturasLoading,
    error: facturasError
  } = useFacturas();

  const { clientes, loadClientes } = useClientes();
  const { productos: allProductos, cachedProductos, searchProductos, isLoading: productosLoading, completedSearchQuery } = useProductos({ salesOnly: true });

  const [factura, setFactura] = useState({
    clienteId: '',
    fecha: todayLocal(),
    fechaVencimiento: '',
    metodoPago: 'COD',
    vendedorId: '',
    notas: '',
    notasInternas: '',
    items: [],
    descuentoFactura: 0,
    costoEnvio: 0,
    // Dirección de envío propia de la factura. Con envioPropio en false se usa
    // la del cliente y no se guarda nada en la factura.
    envioPropio: false,
    envioNombre: '',
    envioDireccion: '',
    envioUnidad: '',
    envioCiudad: '',
    envioEstado: '',
    envioCodigoPostal: '',
    envioPais: '',
    envioResidencial: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedClienteData, setSelectedClienteData] = useState(null);

  // Estados para búsqueda de productos
  const [productSearch, setProductSearch] = useState('');
  const [productTypeFilter, setProductTypeFilter] = useState(DEFAULT_PRODUCT_TYPES);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemError, setItemError] = useState('');

  // Mismas etiquetas que usan los buscadores de preórdenes y órdenes.
  const DEPARTAMENTO_LABELS = {
    BETEL: 'Betel Natural', NATURALANDLAB: 'NaturalAndLab', OTHER_BRANDS: 'Otras Marcas',
    OTRAS_MARCAS: 'Otras Marcas', INGREDIENT: 'Ingredientes', PACKAGING_MATERIAL: 'Material de Empaque'
  };
  const CATEGORIA_LABELS = {
    CAPSULES: 'Cápsulas', POWDERS: 'Polvos', OILS: 'Aceites', TEAS: 'Infusiones',
    CREAMS: 'Cremas', LEAVES: 'Hojas', LIQUIDS: 'Líquidos', SUPPLEMENTS: 'Suplementos',
    TINCTURES: 'Tinturas', TABLETS: 'Tabletas', EXTRACTS: 'Extractos', HERBS: 'Hierbas',
    VITAMINS: 'Vitaminas', MINERALS: 'Minerales', SUPPLIES: 'Insumos', PACKAGING: 'Empaque', OTHER: 'Otros'
  };
  const labelDepartamento = (value) => DEPARTAMENTO_LABELS[String(value || '').toUpperCase()] || value || '';
  const labelCategoria = (value) => CATEGORIA_LABELS[String(value || '').toUpperCase()] || value || '';


  useEffect(() => {
    // Por defecto el hook trae solo 50 clientes y hay más de 200: el cliente de
    // la factura quedaba fuera del select y se veía vacío.
    loadClientes({ limit: 500 });
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      loadFactura();
    }
  }, [id, isEditing]);

  // Efecto para búsqueda de productos
  useEffect(() => {
    if (productSearch.trim().length > 0) {
      searchProductos(productSearch);
      setSearchResults(rankProductMatches(cachedProductos, productSearch));
      setShowDropdown(true);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  }, [productSearch]);

  // Actualizar resultados cuando cambien los productos
  useEffect(() => {
    if (productSearch.trim().length > 0) {
      setSearchResults(rankProductMatches(cachedProductos, productSearch));
    }
  }, [cachedProductos, productSearch]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Los items del API vienen con otra forma que los que arma el formulario.
  // Ojo: el subtotal del API ya viene neto del descuento y aquí se maneja bruto.
  const mapItemFromAPI = (item) => {
    const cantidad = parseFloat(item.quantity) || 0;
    const precioUnitario = parseFloat(item.unit_price) || 0;
    const descuentoMonto = parseFloat(item.discount_amount) || 0;
    const subtotal = cantidad * precioUnitario;

    return {
      productoId: item.product_id,
      nombreProducto: item.description || item.product_name || '',
      codigo: item.product_code || '',
      cantidad,
      precioUnitario,
      descuento: parseFloat(item.discount_percentage) || 0,
      subtotal,
      descuentoMonto,
      impuestos: parseFloat(item.tax_amount) || 0,
      tax_rate: parseFloat(item.tax_rate) || 0,
      unit: item.unit || 'unidad',
      notes: item.notes || '',
      total: subtotal - descuentoMonto + (parseFloat(item.tax_amount) || 0)
    };
  };

  const loadFactura = async () => {
    setIsLoading(true);
    const result = await getFacturaById(id);
    if (result.success) {
      // `data` ya viene traducido al español por transformFacturaFromAPI
      // (hooks/useFacturas.js): clienteId, fecha, costoEnvio, descuento, etc.
      const data = result.data;
      const items = (data.items || []).map(mapItemFromAPI);
      const descuentoItems = items.reduce((sum, item) => sum + item.descuentoMonto, 0);

      setFactura({
        ...data,
        metodoPago: data.metodoPago || 'COD',
        items,
        costoEnvio: parseFloat(data.costoEnvio) || 0,
        // El API guarda el descuento total (items + factura). Lo que excede a la
        // suma de los items es el descuento a nivel de factura.
        descuentoFactura: Math.max(0, (parseFloat(data.descuento) || 0) - descuentoItems),
        envioPropio: Boolean(data.envioPropio),
        envioNombre: data.envioNombre || '',
        envioDireccion: data.envioDireccion || '',
        envioUnidad: data.envioUnidad || '',
        envioCiudad: data.envioCiudad || '',
        envioEstado: data.envioEstado || '',
        envioCodigoPostal: data.envioCodigoPostal || '',
        envioPais: data.envioPais || '',
        envioResidencial: Boolean(data.envioResidencial)
      });

      // Datos del cliente para el recuadro informativo, sin tocar el vencimiento
      if (data.clienteId) {
        loadClienteData(data.clienteId, false);
      }
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  // Cargar datos del cliente cuando se selecciona.
  // `aplicarTerminos` solo va en true cuando el usuario elige el cliente: al
  // abrir una factura existente no se debe recalcular su fecha de vencimiento.
  const loadClienteData = async (clienteId, aplicarTerminos = true) => {
    if (!clienteId) {
      setSelectedClienteData(null);
      return;
    }

    try {
      const result = await clientesService.getCliente(clienteId);

      if (result.success) {
        setSelectedClienteData(result.data);

        if (!aplicarTerminos) {
          // El tipo de pago es un dato del cliente, no de la factura.
          setFactura(prev => ({ ...prev, metodoPago: result.data.payment_type || 'COD' }));
          return;
        }

        // Auto-completar método de pago según el cliente
        const metodoPago = result.data.payment_type || 'TERMINOS';

        // Si el cliente tiene términos de pago, calcular fecha de vencimiento
        setFactura(prev => {
          const updatedFactura = {
            ...prev,
            metodoPago: metodoPago
          };

          if (metodoPago === 'TERMINOS' && result.data.payment_terms) {
            const fechaEmision = new Date(`${prev.fecha}T12:00:00`);
            const diasTerminos = result.data.payment_terms || 30;
            const fechaVenc = new Date(fechaEmision);
            fechaVenc.setDate(fechaVenc.getDate() + diasTerminos);

            updatedFactura.fechaVencimiento = `${fechaVenc.getFullYear()}-${String(fechaVenc.getMonth() + 1).padStart(2, '0')}-${String(fechaVenc.getDate()).padStart(2, '0')}`;
          }

          return updatedFactura;
        });
      } else {
        console.error('Error al cargar datos del cliente:', result.message);
      }
    } catch (err) {
      console.error('Error de conexión al cargar cliente:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFactura(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'fecha' && value && selectedClienteData) {
        const days = selectedClienteData.payment_type === 'COD'
          ? 0
          : Math.max(0, Number(selectedClienteData.payment_terms) || 0);
        const due = new Date(`${value}T12:00:00`);
        if (!Number.isNaN(due.getTime())) {
          due.setDate(due.getDate() + days);
          next.fechaVencimiento = `${due.getFullYear()}-${String(due.getMonth() + 1).padStart(2, '0')}-${String(due.getDate()).padStart(2, '0')}`;
        }
      }
      return next;
    });

    // Si cambia el cliente, cargar sus datos
    if (field === 'clienteId' && value) {
      loadClienteData(value);
    }

    // Limpiar error de validación si existe
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setItemError('');
    setProductSearch(product.nombreProducto);
    setSearchResults([]);
    setShowDropdown(false); // Cerrar dropdown al seleccionar
  };

  // Precio fijo según la categoría del cliente y la Estructura de Precios del
  // producto. Al crear la factura el backend lo vuelve a fijar.
  const categoriaCliente = selectedClienteData?.client_category || null;
  const precioDe = (producto) => precioParaCategoria(producto, categoriaCliente);

  const conPrecio = (item, precioUnitario) => {
    const subtotal = item.cantidad * precioUnitario;
    const descuentoMonto = subtotal * ((item.descuento || 0) / 100);
    return { ...item, precioUnitario, subtotal, descuentoMonto, total: subtotal - descuentoMonto };
  };

  // Si cambia el cliente con productos ya agregados, se recalcula su precio
  useEffect(() => {
    setFactura(prev => ({
      ...prev,
      items: prev.items.map(it => (it.producto ? conPrecio(it, precioDe(it.producto)) : it))
    }));
  }, [categoriaCliente]); // eslint-disable-line

  const addItemToFactura = () => {
    const qty = parseFloat(itemQuantity);
    if (!selectedProduct || !qty || qty <= 0) {
      setItemError('Seleccione un producto y especifique una cantidad válida.');
      return;
    }

    // Aviso interno temporal mientras se completa la migracion del inventario.
    const stockDisponible = Number(selectedProduct.stock);
    const advertenciaStock = Number.isFinite(stockDisponible) && stockDisponible < qty
      ? `Advertencia interna: stock insuficiente (disponible: ${stockDisponible}, solicitado: ${qty}). Se permite agregar mientras termina la migración.`
      : '';

    const newItem = conPrecio({
      productoId: selectedProduct.id,
      nombreProducto: selectedProduct.nombreProducto,
      codigo: selectedProduct.codigo,
      cantidad: qty,
      descuento: 0,
      // Se guarda para recalcular el precio si después cambia el cliente
      producto: selectedProduct
    }, precioDe(selectedProduct));

    setFactura(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setItemError(advertenciaStock);

    // Limpiar formulario de producto
    setSelectedProduct(null);
    setProductSearch('');
    setItemQuantity(1);
    setError(null);
  };

  const updateItemQuantity = (index, value) => {
    const cantidad = Number(value);
    if (!Number.isFinite(cantidad) || cantidad <= 0) return;
    setFactura(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? conPrecio({ ...item, cantidad }, Number(item.precioUnitario) || 0) : item
      )
    }));
  };

  const removeItem = (index) => {
    setFactura(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Misma fórmula que usa el backend al recalcular:
  // total = bruto - descuentos (item + factura) + impuestos + envío
  const calculateTotals = () => {
    const subtotal = factura.items.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);
    const descuentoItems = factura.items.reduce((sum, item) => sum + (parseFloat(item.descuentoMonto) || 0), 0);
    const impuestos = factura.items.reduce((sum, item) => sum + (parseFloat(item.impuestos) || 0), 0);
    const descuentoFactura = parseFloat(factura.descuentoFactura) || 0;
    const costoEnvio = parseFloat(factura.costoEnvio) || 0;
    const descuento = descuentoItems + descuentoFactura;
    const total = subtotal - descuento + impuestos + costoEnvio;

    return { subtotal, descuento, impuestos, costoEnvio, total };
  };

  const validateForm = () => {
    const errors = {};

    if (!factura.clienteId) {
      errors.clienteId = 'Cliente es requerido';
    }
    if (!factura.fecha) {
      errors.fecha = 'Fecha es requerida';
    }
    if (factura.items.length === 0) {
      errors.items = 'Debe agregar al menos un producto';
    }
    // Sin dirección no tiene sentido el envío propio: el backend lo trataría
    // como "usar la del cliente" y el usuario creería que guardó otra.
    if (factura.envioPropio && !String(factura.envioDireccion || '').trim()) {
      errors.envioDireccion = 'Escribe la dirección de envío o vuelve a usar la del cliente';
    }

    // El total no puede quedar por debajo de lo ya abonado: la factura quedaría
    // en sobrepago. El backend también lo rechaza.
    if (isEditing) {
      const abonado = parseFloat(factura.montoAbonado) || 0;
      const { total } = calculateTotals();
      if (abonado > 0 && total < abonado) {
        errors.items = `El total (${formatCurrency(total)}) queda por debajo de lo ya abonado (${formatCurrency(abonado)}). Revierte los pagos antes de reducir el monto.`;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Por favor corrija los errores en el formulario');
      return;
    }

    setIsLoading(true);
    setError(null);

    const totals = calculateTotals();
    // Se envía en español: transformFacturaToAPI (hooks/useFacturas.js) lo
    // traduce a los nombres del API. `descuento` va con el total (items +
    // factura), que es lo que el backend guarda en discount_amount.
    const facturaData = {
      ...factura,
      clienteId: parseInt(factura.clienteId),
      subtotal: totals.subtotal,
      impuestos: totals.impuestos,
      descuento: totals.descuento,
      costoEnvio: parseFloat(factura.costoEnvio) || 0,
      total: totals.total
    };

    console.log('Datos de factura a enviar:', facturaData);

    const result = isEditing
      ? await updateFactura(id, facturaData)
      : await createFactura(facturaData);

    if (result.success) {
      // Al actualizar, regresar al detalle de la misma factura; al crear, al listado.
      navigate(isEditing ? `/system/facturacion/${id}` : '/system/facturacion');
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  const handleCancel = () => {
    navigate('/system/facturacion');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const totals = calculateTotals();

  const clienteFueraDeListado = Boolean(factura.clienteId)
    && !clientes.some(c => String(c.id) === String(factura.clienteId));

  // Mismas reglas que aplica el backend, para no dejar al usuario llenar el
  // formulario y chocar al guardar.
  const bloqueoEdicion = !isEditing
    ? null
    : factura.anulada
      ? 'está anulada.'
      : (parseFloat(factura.saldoPendiente) === 0 && parseFloat(factura.montoAbonado) > 0)
        ? 'ya está pagada. Revierte los pagos si necesitas corregirla.'
        : null;

  if (isLoading || facturasLoading) {
    return <LoadingGrid message={isEditing ? "Cargando factura..." : "Preparando formulario..."} />;
  }

  return (
    <div className="factura-form-page">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={handleCancel}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          {isEditing ? 'Editar Factura' : 'Nueva Factura'}
        </h1>

        <div className="header-actions">
          <button className="btn btn--outline" onClick={handleCancel}>
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isLoading || bloqueoEdicion !== null}
          >
            <FiSave size={16} />
            {isEditing ? 'Actualizar' : 'Crear Factura'}
          </button>
        </div>
      </div>

      {/* Factura que no se puede editar (anulada o pagada) */}
      {bloqueoEdicion && (
        <div className="alert alert--warning">
          <strong>Esta factura no se puede editar:</strong> {bloqueoEdicion}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="factura-form">
        {/* Card 1: Información Principal de la Factura - Diseño Premium */}
        <div className="invoice-header-card">
          <div className="invoice-header-card__left">
            <div className="invoice-icon-circle">
              <FiFileText size={32} />
            </div>
            <div className="invoice-header-info">
              <h3 className="invoice-header-title">
                {isEditing
                  ? `Factura ${factura.numeroFactura || `#${id}`}`
                  : 'Nueva Factura'}
              </h3>
              <p className="invoice-header-subtitle">
                Complete los detalles de la factura
              </p>
            </div>
          </div>

          <div className="invoice-header-card__grid">
            {/* Cliente */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiUser size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Cliente *</label>
                <select
                  className={`invoice-field-input ${validationErrors.clienteId ? 'invoice-field-input--error' : ''}`}
                  value={factura.clienteId}
                  onChange={(e) => handleInputChange('clienteId', parseInt(e.target.value) || '')}
                  required
                >
                  <option value="">Seleccionar cliente...</option>
                  {/* Si el cliente de la factura no está en el listado (inactivo
                      o fuera del tope de la consulta), se agrega su opción para
                      que el select no aparezca vacío. */}
                  {clienteFueraDeListado && (
                    <option value={factura.clienteId}>
                      {factura.clienteNombre || selectedClienteData?.company_name || `Cliente #${factura.clienteId}`}
                    </option>
                  )}
                  {clientes.map(cliente => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombreNegocio}
                    </option>
                  ))}
                </select>
                {validationErrors.clienteId && (
                  <span className="invoice-field-error">{validationErrors.clienteId}</span>
                )}
                {selectedClienteData && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    background: '#f0f9ff',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    color: '#0369a1'
                  }}>
                    <strong>Términos:</strong> {selectedClienteData.payment_type === 'TERMINOS'
                      ? `${selectedClienteData.payment_terms != null ? selectedClienteData.payment_terms : 'No configurados'} días`
                      : 'COD (Pago al instante)'}
                    {selectedClienteData.credit_limit > 0 && (
                      <span style={{ marginLeft: '12px' }}>
                        | <strong>Crédito:</strong> ${selectedClienteData.credit_limit?.toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Dirección de envío */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiMapPin size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Dirección de envío</label>

                {!factura.envioPropio ? (
                  <>
                    <div className="shipping-address-box">
                      {selectedClienteData ? (
                        <>
                          <div>{selectedClienteData.company_name || selectedClienteData.business_name}</div>
                          <div>
                            {selectedClienteData.same_delivery_address === false && selectedClienteData.delivery_address
                              ? selectedClienteData.delivery_address
                              : selectedClienteData.business_address || 'Sin dirección registrada'}
                          </div>
                          <div>
                            {selectedClienteData.same_delivery_address === false && selectedClienteData.delivery_city
                              ? [selectedClienteData.delivery_city, selectedClienteData.delivery_state, selectedClienteData.delivery_postal_code].filter(Boolean).join(', ')
                              : [selectedClienteData.business_city, selectedClienteData.business_state, selectedClienteData.business_postal_code].filter(Boolean).join(', ')}
                          </div>
                        </>
                      ) : (
                        <span className="shipping-address-empty">Selecciona un cliente para ver su dirección</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn-shipping-override"
                      onClick={() => handleInputChange('envioPropio', true)}
                      disabled={bloqueoEdicion !== null}
                    >
                      <FiMapPin size={14} /> Enviar a otra dirección
                    </button>
                  </>
                ) : (
                  <div className="shipping-address-custom">
                    <div className="shipping-address-header">
                      <span>Dirección solo para esta factura</span>
                      <button
                        type="button"
                        className="btn-shipping-reset"
                        onClick={() => handleInputChange('envioPropio', false)}
                        disabled={bloqueoEdicion !== null}
                      >
                        <FiX size={14} /> Usar la del cliente
                      </button>
                    </div>

                    <input
                      type="text"
                      className="invoice-field-input"
                      placeholder="Nombre / destinatario"
                      value={factura.envioNombre}
                      onChange={(e) => handleInputChange('envioNombre', e.target.value)}
                      disabled={bloqueoEdicion !== null}
                    />
                    <input
                      type="text"
                      className="invoice-field-input"
                      placeholder="Dirección (número y calle) *"
                      value={factura.envioDireccion}
                      onChange={(e) => handleInputChange('envioDireccion', e.target.value)}
                      disabled={bloqueoEdicion !== null}
                    />
                    <input
                      type="text"
                      className="invoice-field-input"
                      placeholder="Apartamento, suite, unidad"
                      value={factura.envioUnidad}
                      onChange={(e) => handleInputChange('envioUnidad', e.target.value)}
                      disabled={bloqueoEdicion !== null}
                    />
                    <div className="shipping-address-row">
                      <input
                        type="text"
                        className="invoice-field-input"
                        placeholder="Ciudad"
                        value={factura.envioCiudad}
                        onChange={(e) => handleInputChange('envioCiudad', e.target.value)}
                        disabled={bloqueoEdicion !== null}
                      />
                      <input
                        type="text"
                        className="invoice-field-input"
                        placeholder="Estado"
                        value={factura.envioEstado}
                        onChange={(e) => handleInputChange('envioEstado', e.target.value)}
                        disabled={bloqueoEdicion !== null}
                      />
                      <input
                        type="text"
                        className="invoice-field-input"
                        placeholder="Código postal"
                        value={factura.envioCodigoPostal}
                        onChange={(e) => handleInputChange('envioCodigoPostal', e.target.value)}
                        disabled={bloqueoEdicion !== null}
                      />
                    </div>
                    <input
                      type="text"
                      className="invoice-field-input"
                      placeholder="País"
                      value={factura.envioPais}
                      onChange={(e) => handleInputChange('envioPais', e.target.value)}
                      disabled={bloqueoEdicion !== null}
                    />
                    <label className="shipping-residential">
                      <input
                        type="checkbox"
                        checked={factura.envioResidencial}
                        onChange={(e) => handleInputChange('envioResidencial', e.target.checked)}
                        disabled={bloqueoEdicion !== null}
                      />
                      Es dirección residencial
                    </label>

                    {validationErrors.envioDireccion && (
                      <span className="invoice-field-error">{validationErrors.envioDireccion}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Fecha */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiCalendar size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Fecha de Emisión *</label>
                <input
                  type="date"
                  className={`invoice-field-input ${validationErrors.fecha ? 'invoice-field-input--error' : ''}`}
                  value={factura.fecha}
                  onChange={(e) => handleInputChange('fecha', e.target.value)}
                  required
                />
                {validationErrors.fecha && (
                  <span className="invoice-field-error">{validationErrors.fecha}</span>
                )}
              </div>
            </div>

            {/* Fecha Vencimiento */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiCalendar size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Fecha de Vencimiento</label>
                <input
                  type="date"
                  className="invoice-field-input"
                  value={factura.fechaVencimiento}
                  onChange={(e) => handleInputChange('fechaVencimiento', e.target.value)}
                />
              </div>
            </div>

            {/* Método de Pago */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiCreditCard size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Tipos de Pago</label>
                <div className="invoice-field-value" aria-label="Tipo de pago del cliente">
                  {selectedClienteData
                    ? (selectedClienteData.payment_type === 'TERMINOS'
                        ? `Términos: ${selectedClienteData.payment_terms != null ? selectedClienteData.payment_terms + ' días' : 'días no configurados'}`
                        : selectedClienteData.payment_type === 'COD'
                          ? 'COD (Pago antes de enviar la orden)'
                          : 'No especificado')
                    : 'Cargando tipo de pago...'}
                </div>
              </div>
            </div>

            {/* Notas - Ocupa 2 columnas */}
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas de la Orden</label>
                <textarea
                  className="invoice-field-input invoice-field-textarea"
                  placeholder="Notas adicionales sobre la factura..."
                  value={factura.notas}
                  onChange={(e) => handleInputChange('notas', e.target.value)}
                  rows="2"
                />
                <label className="invoice-field-label">Notas Internas (solo equipo Betel)</label>
                <textarea className="invoice-field-input invoice-field-textarea" value={factura.notasInternas || ''} onChange={(e) => handleInputChange('notasInternas', e.target.value)} rows="2" />
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Tabla de Factura - Diseño Profesional */}
        {factura.items.length > 0 ? (
          <div className="invoice-table-card">
            <div className="invoice-table-header">
              <div className="table-header-icon">
                <FiShoppingCart size={24} />
              </div>
              <div className="table-header-content">
                <h3 className="table-header-title">Productos en la Factura</h3>
                <p className="table-header-subtitle">{factura.items.length} producto(s) agregado(s)</p>
              </div>
            </div>

            <div className="invoice-table-wrapper">
              <table className="invoice-table">
                <thead className="invoice-table-head">
                  <tr>
                    <th className="th-product">Producto</th>
                    <th className="th-qty">Cantidad</th>
                    <th className="th-price">Precio Unit.</th>
                    <th className="th-discount">Desc.</th>
                    <th className="th-total">Total</th>
                    <th className="th-actions"></th>
                  </tr>
                </thead>
                <tbody className="invoice-table-body">
                  {factura.items.map((item, index) => (
                    <tr key={index} className="invoice-table-row">
                      <td className="td-product">
                        <div className="product-cell">
                          <div className="product-cell-icon">
                            <FiPackage size={16} />
                          </div>
                          <div className="product-cell-info">
                            <div className="product-cell-name">{item.nombreProducto}</div>
                            <div className="product-cell-code">{item.codigo}</div>
                          </div>
                        </div>
                      </td>
                      <td className="td-qty">
                        <input type="number" min="0.1" step="any" className="qty-badge"
                          aria-label={`Cantidad de ${item.nombreProducto}`}
                          style={{ width: '76px', padding: '7px', border: '1px solid #ad78bb', borderRadius: '8px', color: '#782b8e', background: '#fff' }}
                          value={item.cantidad}
                          onChange={(e) => updateItemQuantity(index, e.target.value)} />
                      </td>
                      <td className="td-price">
                        <span className="price-value">{formatCurrency(item.precioUnitario)}</span>
                      </td>
                      <td className="td-discount">
                        {item.descuento > 0 ? (
                          <span className="discount-badge">{item.descuento}%</span>
                        ) : (
                          <span className="no-discount">—</span>
                        )}
                      </td>
                      <td className="td-total">
                        <span className="total-value">{formatCurrency(item.total)}</span>
                      </td>
                      <td className="td-actions">
                        <button
                          type="button"
                          className="btn-icon-delete"
                          onClick={() => removeItem(index)}
                          title="Eliminar producto"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totales - Diseño Profesional */}
            <div className="invoice-totals-section">
              <div className="totals-container">
                <div className="totals-row">
                  <span className="totals-label">Subtotal</span>
                  <span className="totals-value">{formatCurrency(totals.subtotal)}</span>
                </div>
                {totals.descuento > 0 && (
                  <div className="totals-row totals-row--discount">
                    <span className="totals-label">Descuento</span>
                    <span className="totals-value">-{formatCurrency(totals.descuento)}</span>
                  </div>
                )}
                {totals.impuestos > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">Impuestos</span>
                    <span className="totals-value">{formatCurrency(totals.impuestos)}</span>
                  </div>
                )}
                <div className="totals-row">
                  <span className="totals-label">Descuento de factura</span>
                  <input
                    type="number"
                    className="totals-input"
                    min="0"
                    step="0.01"
                    value={factura.descuentoFactura}
                    onChange={(e) => handleInputChange('descuentoFactura', e.target.value)}
                    disabled={bloqueoEdicion !== null}
                  />
                </div>
                <div className="totals-row">
                  <span className="totals-label">Costo de envío</span>
                  <input
                    type="number"
                    className="totals-input"
                    min="0"
                    step="0.01"
                    value={factura.costoEnvio}
                    onChange={(e) => handleInputChange('costoEnvio', e.target.value)}
                    disabled={bloqueoEdicion !== null}
                  />
                </div>
                <div className="totals-divider"></div>
                <div className="totals-row totals-row--final">
                  <span className="totals-label">Total</span>
                  <span className="totals-value">{formatCurrency(totals.total)}</span>
                </div>
                {isEditing && factura.montoAbonado > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">Ya abonado</span>
                    <span className="totals-value">-{formatCurrency(factura.montoAbonado)}</span>
                  </div>
                )}
              </div>
            </div>

            {validationErrors.items && (
              <div className="invoice-table-error">{validationErrors.items}</div>
            )}
          </div>
        ) : (
          <div className="invoice-empty-state">
            <div className="empty-state-icon">
              <FiShoppingCart size={64} />
            </div>
            <h3 className="empty-state-title">No hay productos agregados</h3>
            <p className="empty-state-message">
              Use el buscador de productos para encontrar y agregar ítems a la factura
            </p>
            {validationErrors.items && (
              <div className="invoice-table-error">{validationErrors.items}</div>
            )}
          </div>
        )}
        {/* Card 2: Buscador de Productos - Diseño Moderno */}
        <div className="product-search-card">
          <div className="product-search-header">
            <div className="search-header-icon">
              <FiPackage size={24} />
            </div>
            <div className="search-header-content">
              <h3 className="search-header-title">Agregar Productos</h3>
              <p className="search-header-subtitle">Busque y agregue productos a la factura</p>
            </div>
          </div>

          <div className="product-search-body">
            <ProductSourceCheckboxes value={productTypeFilter} onChange={(nextValue) => {
              setProductTypeFilter(nextValue);
              setSearchResults(rankProductMatches(cachedProductos, productSearch));
              setShowDropdown(true);
            }} />
            {/* Buscador Principal */}
            <div className="search-combo-wrapper" ref={dropdownRef} onKeyDownCapture={e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); setShowDropdown(false); e.target.blur?.(); } }}>
              <div className="search-combo-icon-left">
                <FiSearch size={20} />
              </div>
              <input
                type="text"
                className="search-combo-input"
                placeholder="Buscar por nombre de producto o código..."
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  setSelectedProduct(null);
                  setItemError('');
                  setError(null);
                }}
                onFocus={() => {
                  // Permitir elegir productos aun antes de escribir una busqueda.
                  if (!productSearch.trim()) setSearchResults(rankProductMatches(cachedProductos, productSearch));
                  setShowDropdown(true);
                }}
              />
              {productosLoading && (
                <div className="search-combo-loading">
                  <div className="spinner-sm"></div>
                </div>
              )}

              {/* Selector compartido: clic agrega, cantidad actualiza, check confirma. */}
              {showDropdown && (
                <div className="product-dropdown">
                  <MultiProductPicker
                    products={rankProductMatches(cachedProductos, productSearch).filter(product => matchesProductTypes(product, productTypeFilter))}
                    renderMeta={product => <>
                      {product.departamento && <span className="meta-code">{labelDepartamento(product.departamento)}</span>}
                      {product.departamento && product.categoria && <span className="meta-separator">·</span>}
                      {product.categoria && <span>{labelCategoria(product.categoria)}</span>}
                      {(product.departamento || product.categoria) && <span className="meta-separator">·</span>}
                      <span className="meta-stock">Stock: {product.stock}</span>
                    </>}
                    onAdd={entries => {
                      setFactura(prev => ({
                        ...prev,
                        items: entries.reduce((acc, { product, quantity }) => {
                          const existing = acc.findIndex(item => item.productoId === product.id);
                          if (existing >= 0) {
                            const next = [...acc];
                            const newQty = (Number(next[existing].cantidad) || 0) + quantity;
                            if (newQty <= 0) return next.filter((_, index) => index !== existing);
                            next[existing] = conPrecio({ ...next[existing], cantidad: newQty }, Number(next[existing].precioUnitario) || precioDe(product));
                            return next;
                          }
                          if (quantity <= 0) return acc;
                          return [...acc, conPrecio({
                            productoId: product.id, nombreProducto: product.nombreProducto,
                            codigo: product.codigo, cantidad: quantity, descuento: 0, producto: product
                          }, precioDe(product))];
                        }, prev.items)
                      }));
                      setItemError('');
                      setError(null);
                    }}
                  />
                  {!productosLoading && completedSearchQuery === productSearch.trim() && rankProductMatches(cachedProductos, productSearch).filter(product => matchesProductTypes(product, productTypeFilter)).length === 0 && (
                    <div className="product-dropdown-item">No hay productos disponibles con este filtro.</div>
                  )}
                </div>
              )}
            </div>

            {/* Vista previa del producto seleccionado */}
            {selectedProduct && (
              <div className="selected-product-card">
                <div className="selected-product-info">
                  <div className="selected-product-badge">
                    <FiPackage size={16} />
                  </div>
                  <div className="selected-product-details">
                    <h4 className="selected-product-name">{selectedProduct.nombreProducto}</h4>
                    <div className="selected-product-meta">
                      <span className="meta-item">Código: {selectedProduct.codigo}</span>
                      <span className="meta-separator">•</span>
                      <span className="meta-item">Precio: {formatCurrency(precioDe(selectedProduct))}</span>
                      <span className="meta-separator">•</span>
                      <span className="meta-item">Stock: {selectedProduct.stock}</span>
                    </div>
                  </div>
                </div>

                <div className="selected-product-controls">
                  <div className="control-group">
                    <label className="control-label">Cantidad</label>
                    <input
                      type="number"
                      value={itemQuantity}
                      onChange={(e) => setItemQuantity(e.target.value)}
                      min="0.1"
                      step="0.1"
                      className="control-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn--primary btn--add-product"
                    onClick={addItemToFactura}
                  >
                    <FiPlus size={18} />
                    Agregar
                  </button>
                </div>
                {itemError && <p role="alert" style={{ color: '#b42318', marginTop: 12 }}>{itemError}</p>}
              </div>
            )}
          </div>
        </div>

        <div className="factura-bottom-save" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', marginTop: '24px', padding: '20px 0 32px', borderTop: '1px solid #e5e7eb', position: 'relative', zIndex: 1 }}>
          <button type="submit" className="btn btn--primary" disabled={isLoading || bloqueoEdicion !== null}>
            <FiSave size={16} /> {isEditing ? 'Actualizar' : 'Crear Factura'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FacturaForm;
